// BannerCarousel.tsx
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Container, Box, Skeleton } from "@mui/material";
import "./BannerCarousel.css";
import { serviceFetchBanners } from "../../api/service/services";
import { AxiosResponse } from "axios";
import defaultImage from "../../assets/default_sale.png"; // ✅ fallback image
import { getRetailerLogo } from "src/utils/retailers_list";

const BannerCarousel: React.FC = () => {
  const [bannerData, setBannerData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    serviceFetchBanners()
      .then((response: AxiosResponse) => {
        const validBanners = (response?.data || [])
          // ✅ Only banners that have a valid URL
          .filter(
            (banner: any) =>
              banner?.acf?.banner_url &&
              banner?.acf?.banner_url.trim() !== ""
          )
          // ✅ Replace missing/empty images with default
          .map((banner: any) => {
            const image = banner?.acf?.banner_image;
            if (!image || image.trim() === "") {
              banner.acf.banner_image = defaultImage;
            }
            return banner;
          });

        setBannerData(validBanners);
      })
      .finally(() => setLoading(false));
  }, []);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    waitForAnimate: false,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: false,
  };

  // 🟡 Show skeleton only while loading and before data arrives
  if (loading && bannerData.length === 0) {
    return (
      <Container maxWidth={false} disableGutters className="container">
        <Skeleton
          variant="rounded"
          height={600}
          animation="wave"
          sx={{ borderRadius: "20px" }}
        />
      </Container>
    );
  }

  // 🔴 Hide everything if no valid data after loading
  if (!loading && bannerData.length === 0) {
    return null;
  }

  // 🟢 Render slider when valid banners exist
  return (
    <Container maxWidth={false} disableGutters className="banner-container">
      <Slider {...settings}>
        {bannerData.map((banner, index) => {
          const imageUrl = banner?.acf?.banner_image;
          const targetUrl = banner?.acf?.banner_url;
          const altText = banner?.acf?.banner_alt || `Banner ${index + 1}`;
          const retailer = banner?.acf?.banner_retailer;
          const retailerLogo = getRetailerLogo(retailer);
          return (
            <Box
              key={banner?.id || index}
              className="slider-image-container"
              sx={{
                position: "relative",
                borderRadius: "20px",
                //  overflow: "hidden",
              }}
            >
              {/* Main Banner Image */}
              <Box
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={imageUrl}
                  alt={altText}
                  onClick={() => targetUrl && window.open(targetUrl, "_blank")}
                  sx={{
                    cursor: targetUrl ? "pointer" : "default",
                    width: "100%",
                    height: "auto",
                    display: "block",
                    transition: "transform 0.4s ease",
                  }}
                />
              </Box>

              {/* Retailer Logo Overlay - Bottom Left */}
              {retailer && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    left: 16,
                    backgroundColor: "rgba(255,255,255,0.7)",
                    borderRadius: "50%",
                    padding: "8px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <Box
                    component="img"
                    src={retailerLogo}
                    alt={`${retailer} logo`}
                    sx={{
                      width: { xs: 40, sm: 40, md: 140 },
                      height: { xs: 40, sm: 40, md: 140 },
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Slider>
    </Container>
  );
};

export default BannerCarousel;
